import React, { useState, useEffect } from 'react';
import { Button } from '../../ui/Button';
import { Input } from '../../ui/Input';
import { TextArea } from '../../ui/TextArea';
import { leadService } from '../../../services/leadService';
import { Spinner } from '../../ui/Spinner';
import { isPast, isToday, format, startOfDay } from 'date-fns';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import { ConnectionMoments } from './ConnectionMoments';
import { PersonFollowUps } from '../followups/PersonFollowUps';

export const ConnectionDetailModal = ({ lead, user, onClose, onUpdate }) => {
  const [activeTab, setActiveTab] = useState('relationship'); // 'relationship' | 'moments' | 'timeline'
  const [status, setStatus] = useState(lead.status);
  const [notes, setNotes] = useState(lead.owner_notes || '');
  const [savingNotes, setSavingNotes] = useState(false);
  const [isImportant, setIsImportant] = useState(lead.is_important || false);
  
  // Tags
  const [tagInput, setTagInput] = useState('');
  const [tags, setTags] = useState(lead.tags || []);
  
  // Outcomes
  const [outcomes, setOutcomes] = useState(lead.outcomes || []);
  const [outcomeType, setOutcomeType] = useState('client');
  const [outcomeNote, setOutcomeNote] = useState('');
  const [outcomeDate, setOutcomeDate] = useState(new Date().toISOString().split('T')[0]);
  const [savingOutcome, setSavingOutcome] = useState(false);

  // Timeline
  const [timeline, setTimeline] = useState([]);
  const [loadingTimeline, setLoadingTimeline] = useState(false);

  useEffect(() => {
    if (activeTab === 'timeline') {
      loadTimeline();
    }
  }, [activeTab]);

  const loadTimeline = async () => {
    setLoadingTimeline(true);
    try {
      const data = await leadService.getTimeline(lead.id, user.id);
      setTimeline(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingTimeline(false);
    }
  };

  const handleNotesBlur = async () => {
    if (notes === (lead.owner_notes || '')) return;
    setSavingNotes(true);
    try {
      const updated = await leadService.updateLead(lead.id, user.id, { owner_notes: notes });
      onUpdate(updated);
    } catch (err) {
      console.error('Failed to save notes:', err);
    } finally {
      setSavingNotes(false);
    }
  };

  const handleStatusChange = async (e) => {
    const newStatus = e.target.value;
    try {
      const updated = await leadService.updateLead(lead.id, user.id, { status: newStatus });
      setStatus(newStatus);
      onUpdate(updated);
    } catch (err) {
      alert('Failed to update status.');
    }
  };

  const toggleImportant = async () => {
    const newVal = !isImportant;
    setIsImportant(newVal);
    try {
      const updated = await leadService.updateLead(lead.id, user.id, { is_important: newVal });
      onUpdate(updated);
    } catch (err) {
      setIsImportant(!newVal);
      console.error(err);
    }
  };

  const handleAddTag = async (e) => {
    if (e.key === 'Enter' && tagInput.trim()) {
      e.preventDefault();
      try {
        await leadService.addTag(lead.id, tagInput.trim());
        setTagInput('');
        const updated = await leadService.updateLead(lead.id, user.id, {});
        setTags(updated.tags);
        onUpdate(updated);
      } catch (err) {
        console.error('Failed to add tag:', err);
      }
    }
  };

  const handleRemoveTag = async (tagId) => {
    try {
      await leadService.removeTag(lead.id, tagId);
      const updatedTags = tags.filter(t => t.id !== tagId);
      setTags(updatedTags);
      onUpdate({ ...lead, tags: updatedTags });
    } catch (err) {
      console.error('Failed to remove tag:', err);
    }
  };

  // Follow-ups are managed by PersonFollowUps; refresh the lead so list badges stay current.
  const handleFollowUpsChanged = async () => {
    try {
      const updated = await leadService.updateLead(lead.id, user.id, {});
      onUpdate(updated);
    } catch (err) {
      console.error('Failed to refresh follow-ups:', err);
    }
  };

  const handleAddOutcome = async () => {
    setSavingOutcome(true);
    try {
      const newOutcome = await leadService.addOutcome(lead.id, user.id, outcomeType, outcomeNote, outcomeDate);
      const newOutcomes = [newOutcome, ...outcomes];
      setOutcomes(newOutcomes);
      setOutcomeNote('');
      onUpdate({ ...lead, outcomes: newOutcomes });
    } catch (err) {
      console.error('Failed to add outcome:', err);
    } finally {
      setSavingOutcome(false);
    }
  };

  const handleRemoveOutcome = async (outcomeId) => {
    try {
      await leadService.removeOutcome(outcomeId, user.id);
      const newOutcomes = outcomes.filter(o => o.id !== outcomeId);
      setOutcomes(newOutcomes);
      onUpdate({ ...lead, outcomes: newOutcomes });
    } catch (err) {
      console.error('Failed to remove outcome:', err);
    }
  };

  const getStatusColor = (s) => {
    const colors = {
      new: 'bg-blue-100 text-blue-800',
      contacted: 'bg-amber-100 text-amber-800',
      following_up: 'bg-purple-100 text-purple-800',
      active: 'bg-indigo-100 text-indigo-800',
      qualified: 'bg-emerald-100 text-emerald-800',
      client: 'bg-green-100 text-green-800',
      closed: 'bg-slate-100 text-slate-800'
    };
    return colors[s] || colors.new;
  };

  const formatEventLabel = (event) => {
    switch (event.event_type) {
      case 'status_changed': return `Status changed from ${event.metadata?.old} to ${event.metadata?.new}`;
      case 'importance_changed': return event.metadata?.important ? 'Marked as important' : 'Removed importance';
      case 'note_updated': return 'Notes updated';
      case 'tag_added': return `Added tag: ${event.metadata?.tag}`;
      case 'tag_removed': return `Removed tag: ${event.metadata?.tag}`;
      case 'reminder_created': return `Scheduled follow-up for ${new Date(event.metadata?.due_date).toLocaleDateString()}`;
      case 'reminder_completed': return 'Completed follow-up';
      case 'reminder_rescheduled': return `Rescheduled follow-up to ${new Date(event.metadata?.due_date).toLocaleDateString()}`;
      case 'outcome_added': return `Achieved outcome: ${event.metadata?.outcome}`;
      case 'outcome_removed': return `Removed outcome: ${event.metadata?.outcome}`;
      default: return event.event_type.replace('_', ' ');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm sm:items-center items-end">
      <div className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-5xl shadow-2xl animate-in slide-in-from-bottom-8 sm:zoom-in-95 duration-200 flex flex-col h-[90vh] sm:h-auto sm:max-h-[90vh]">
        <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center shrink-0 bg-slate-50 rounded-t-3xl">
          <div className="flex items-center gap-3">
            <h3 className="font-bold text-slate-900 text-lg">Connection Profile</h3>
            <span className={`capitalize text-xs font-medium px-2.5 py-0.5 rounded-full ${getStatusColor(status)}`}>
              {status.replace('_', ' ')}
            </span>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900 transition-colors p-1" aria-label="Close details">
            <SafeIcon icon={FiIcons.FiX} className="text-2xl" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto hide-scrollbar flex flex-col md:flex-row">
          {/* Left Column: Contact Details & Context */}
          <div className="w-full md:w-[45%] p-6 border-r border-slate-100 bg-white">
            <div className="flex justify-between items-start mb-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full overflow-hidden bg-slate-100 border border-slate-200 shrink-0 flex items-center justify-center text-slate-400">
                  {lead.contact_photo_url ? (
                    <img src={lead.contact_photo_url} alt={lead.full_name} className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-2xl font-bold text-slate-400">{lead.full_name.charAt(0).toUpperCase()}</span>
                  )}
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-slate-900 pr-4">{lead.full_name}</h2>
                  {(lead.job_title || lead.company_name) && (
                    <p className="text-slate-600 mt-1">
                      {lead.job_title} {lead.company_name ? `at ${lead.company_name}` : ''}
                    </p>
                  )}
                </div>
              </div>
              <button 
                onClick={toggleImportant}
                className={`p-2 rounded-full transition-colors ${isImportant ? 'bg-amber-50 text-amber-500' : 'bg-slate-50 text-slate-400 hover:bg-slate-100 hover:text-slate-600'}`}
                title={isImportant ? "Unmark Important" : "Mark as Important"}
              >
                <SafeIcon icon={FiIcons.FiStar} className={isImportant ? 'fill-current' : ''} />
              </button>
            </div>

            <div className="space-y-3 mb-8">
              <a href={`mailto:${lead.email}`} className="flex items-center p-3 rounded-xl bg-slate-50 hover:bg-slate-100 border border-slate-100 transition-colors group">
                <SafeIcon icon={FiIcons.FiMail} className="text-slate-400 group-hover:text-slate-900 mr-3 shrink-0" />
                <span className="text-sm font-medium text-slate-900 truncate">{lead.email}</span>
              </a>
              {lead.phone && (
                <a href={`tel:${lead.phone}`} className="flex items-center p-3 rounded-xl bg-slate-50 hover:bg-slate-100 border border-slate-100 transition-colors group">
                  <SafeIcon icon={FiIcons.FiPhone} className="text-slate-400 group-hover:text-slate-900 mr-3 shrink-0" />
                  <span className="text-sm font-medium text-slate-900">{lead.phone}</span>
                </a>
              )}
            </div>

            {lead.message && (
              <div className="mb-8">
                <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Original Message</h4>
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{lead.message}</p>
                </div>
              </div>
            )}

            <div className="text-xs text-slate-500 space-y-2 bg-slate-50 p-4 rounded-xl border border-slate-100 mt-auto">
              <p className="flex justify-between"><span>Connected On:</span> <span className="font-medium text-slate-700">{new Date(lead.created_at).toLocaleDateString()}</span></p>
              <p className="flex justify-between"><span>Shared Via:</span> <span className="font-medium text-slate-700">{lead.cards?.internal_name || 'Unknown Card'}</span></p>
              <p className="flex justify-between"><span>Source:</span> <span className="font-medium text-slate-700 capitalize">{lead.source?.replace('_', ' ')}</span></p>
              <p className="flex justify-between pt-2 border-t border-slate-200 mt-2">
                <span>Last Interaction:</span> <span className="font-medium text-slate-700">{lead.last_interaction_at ? new Date(lead.last_interaction_at).toLocaleDateString() : 'Never'}</span>
              </p>
            </div>
          </div>

          {/* Right Column: Dynamic Content */}
          <div className="w-full md:w-[55%] flex flex-col bg-slate-50/50">
            <div className="flex border-b border-slate-200 px-6 pt-4 bg-white shrink-0 overflow-x-auto hide-scrollbar">
              <button 
                onClick={() => setActiveTab('relationship')} 
                className={`pb-3 px-4 text-sm font-bold border-b-2 whitespace-nowrap transition-colors ${activeTab === 'relationship' ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
              >
                Intelligence
              </button>
              <button 
                onClick={() => setActiveTab('moments')} 
                className={`pb-3 px-4 text-sm font-bold border-b-2 whitespace-nowrap transition-colors ${activeTab === 'moments' ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
              >
                Moments
              </button>
              <button 
                onClick={() => setActiveTab('timeline')} 
                className={`pb-3 px-4 text-sm font-bold border-b-2 whitespace-nowrap transition-colors ${activeTab === 'timeline' ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
              >
                Timeline
              </button>
            </div>

            <div className="p-6 flex-1 overflow-y-auto">
              {activeTab === 'relationship' ? (
                <div className="space-y-8">
                  {/* Outcomes (Intelligence) */}
                  <div>
                    <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Relationship Outcomes</h4>
                    {outcomes.length > 0 && (
                      <div className="space-y-2 mb-4">
                        {outcomes.map(o => (
                          <div key={o.id} className="bg-white p-3 rounded-xl border border-emerald-100 flex justify-between items-start shadow-sm">
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <SafeIcon icon={FiIcons.FiAward} className="text-emerald-500" />
                                <span className="font-bold text-slate-900 capitalize">{o.outcome_type.replace('_', ' ')}</span>
                                <span className="text-xs text-slate-400">• {new Date(o.outcome_date).toLocaleDateString()}</span>
                              </div>
                              {o.note && <p className="text-xs text-slate-600 pl-6">{o.note}</p>}
                            </div>
                            <button onClick={() => handleRemoveOutcome(o.id)} className="text-slate-400 hover:text-red-500 transition-colors p-1">
                              <SafeIcon icon={FiIcons.FiTrash2} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col gap-3">
                      <div className="flex gap-3">
                        <select 
                          className="w-1/3 rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-slate-900 outline-none bg-white capitalize"
                          value={outcomeType}
                          onChange={(e) => setOutcomeType(e.target.value)}
                        >
                          {['client', 'partner', 'investor', 'candidate', 'employer', 'mentor', 'friend', 'other'].map(t => (
                            <option key={t} value={t}>{t}</option>
                          ))}
                        </select>
                        <Input type="date" value={outcomeDate} onChange={(e) => setOutcomeDate(e.target.value)} className="w-2/3" />
                      </div>
                      <div className="flex gap-3">
                        <Input placeholder="Private note about this outcome..." value={outcomeNote} onChange={(e) => setOutcomeNote(e.target.value)} className="flex-1" />
                        <Button onClick={handleAddOutcome} isLoading={savingOutcome} size="sm" className="shrink-0">Record Outcome</Button>
                      </div>
                    </div>
                  </div>

                  {/* Status & Tags */}
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Status</h4>
                      <select 
                        className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm font-medium focus:ring-2 focus:ring-slate-900 outline-none bg-white capitalize"
                        value={status}
                        onChange={handleStatusChange}
                      >
                        {['new', 'contacted', 'following_up', 'active', 'qualified', 'client', 'closed'].map(s => (
                          <option key={s} value={s}>{s.replace('_', ' ')}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Tags</h4>
                      <div className="flex flex-wrap gap-2 mb-2">
                        {tags.map(tag => (
                          <span key={tag.id} className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-slate-200 text-slate-700">
                            {tag.name}
                            <button onClick={() => handleRemoveTag(tag.id)} className="ml-1 text-slate-400 hover:text-red-500">
                              <SafeIcon icon={FiIcons.FiX} />
                            </button>
                          </span>
                        ))}
                      </div>
                      <Input placeholder="Add tag + Enter" value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyDown={handleAddTag} className="text-sm" />
                    </div>
                  </div>

                  {/* Follow-ups */}
                  <PersonFollowUps
                    user={user}
                    contactId={lead.contact_id}
                    connectionId={lead.id}
                    onChanged={handleFollowUpsChanged}
                  />

                  {/* Notes */}
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Private Notes</h4>
                      {savingNotes && <span className="text-xs text-slate-400 animate-pulse flex items-center"><SafeIcon icon={FiIcons.FiRefreshCw} className="mr-1 animate-spin" /> Saving</span>}
                    </div>
                    <TextArea 
                      value={notes} 
                      onChange={e => setNotes(e.target.value)} 
                      onBlur={handleNotesBlur}
                      placeholder="Add private context about this relationship..." 
                      rows={6} 
                      className="bg-yellow-50/30 focus:bg-white resize-none"
                    />
                  </div>
                </div>
              ) : activeTab === 'moments' ? (
                <ConnectionMoments lead={lead} user={user} />
              ) : (
                <div className="space-y-6">
                  {loadingTimeline ? (
                    <div className="flex justify-center py-10"><Spinner size="sm" /></div>
                  ) : timeline.length === 0 ? (
                    <p className="text-center text-sm text-slate-500 py-10">No activity recorded yet.</p>
                  ) : (
                    <div className="relative border-l-2 border-slate-200 ml-3 space-y-6 pb-4">
                      {timeline.map((event, idx) => (
                        <div key={event.id} className="relative pl-6">
                          <span className="absolute -left-[9px] top-1 w-4 h-4 rounded-full border-2 border-white bg-slate-300"></span>
                          <p className="text-sm font-medium text-slate-900 capitalize">{formatEventLabel(event)}</p>
                          <p className="text-xs text-slate-500 mt-0.5">{new Date(event.created_at).toLocaleString()}</p>
                        </div>
                      ))}
                      <div className="relative pl-6">
                        <span className="absolute -left-[9px] top-1 w-4 h-4 rounded-full border-2 border-white bg-blue-500"></span>
                        <p className="text-sm font-medium text-slate-900">Connection established</p>
                        <p className="text-xs text-slate-500 mt-0.5">{new Date(lead.created_at).toLocaleString()}</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};